// Full-page view for a chat assistant query result. Opened when the
// user clicks "View in full page" on a chat result that doesn't fit
// well inside the cramped chat drawer column. The chat drawer is
// rendered in the global Header and stays open across navigation, so
// the user sees the wide table here with the conversation still
// anchored on the right.
import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';

import { GetCustomerSettingsResponse } from '../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { StoneXRow } from '../../../StoneX';
import StoneXMainPage from '../../../StoneX/StoneXMainPage/StoneXMainPage';

import { CHAT_QUERY_REGISTRY } from './ChatQueryRegistry';
import { useChatResults } from './ChatResultsContext';

export default function ChatResultsPage() {
    const { activeResult } = useChatResults();
    const customerSettings = useApplicationSelector(
        (state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings
    );
    const navigate = useNavigate();

    if (!activeResult) {
        return (
            <StoneXMainPage>
                <StoneXRow>
                    <h1>Chat Results</h1>
                </StoneXRow>
                <Paper variant="outlined" sx={{ p: 3, mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ChatIcon color="action" />
                    <Typography variant="body1" color="text.secondary">
                        No chat result selected yet. Open the Data Assistant from the header, ask a question,
                        then click <strong>View in full page</strong> on the result to see it here.
                    </Typography>
                </Paper>
            </StoneXMainPage>
        );
    }

    const descriptor = CHAT_QUERY_REGISTRY.find(d => d.key === activeResult.descriptorKey);

    if (!descriptor || !customerSettings) {
        return (
            <StoneXMainPage>
                <StoneXRow>
                    <h1>Chat Results</h1>
                </StoneXRow>
                <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                    <Typography color="error">
                        {descriptor
                            ? 'Customer settings are not loaded yet, please try again.'
                            : `Unknown chat result type "${activeResult.descriptorKey}".`}
                    </Typography>
                </Paper>
            </StoneXMainPage>
        );
    }

    // Re-use the same table component the chat embeds so the user sees a
    // familiar layout - just at the page's full width instead of the
    // ~350px chat column. No closeDrawer is supplied because the drawer
    // is meant to stay open beside this page.
    const tableElement = descriptor.render(activeResult.rows, {
        customerSettings,
        navigate,
    });

    const title = descriptor.plural.charAt(0).toUpperCase() + descriptor.plural.slice(1);

    return (
        <StoneXMainPage>
            <StoneXRow>
                <h1>Chat Results: {title}</h1>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/commodity-management/${descriptor.key}`)}
                    sx={{ ml: 'auto' }}
                >
                    Open full {descriptor.singular} page
                </Button>
            </StoneXRow>
            {activeResult.filterEcho && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                    dangerouslySetInnerHTML={{
                        __html: activeResult.filterEcho.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                    }}
                />
            )}
            <Typography variant="body1" sx={{ mb: 2 }}>
                {activeResult.summary}
            </Typography>
            <Box sx={{ width: '100%' }}>{tableElement}</Box>
        </StoneXMainPage>
    );
}
